import { Component, OnDestroy, OnInit } from '@angular/core';
import { DashboardService } from '../../core/services/dashboard.service';
import { LoadingService } from '../../core/services/loading.service';
import * as moment from 'moment';
import { SETTINGS } from '../../core/config/common.settings';
import { AuthService } from '../../modules/auth/services/auth.service';
import { UserService } from '../../core/services/user.service';
import { Router } from '@angular/router';
import { SettingService } from '../../core/services/setting.service';
import { ChargebeeService } from '../../core/services/chargebee.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { PosthogService } from '../../core/services/posthog.service';
import { PrivilegeService } from '../../core/services/privilege.service';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslocoEvents } from '@ngneat/transloco';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { environment } from '../../../environments/environment';
import { Step, GetStartedMetrics } from './dashboard.types';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  itineraries: any[] = [];
  itineraryCount: any;
  salesByUsers: any;
  getStartedMetrics: GetStartedMetrics | null = null;
  steps: Step[] = [];
  dateFormat = 'dd MMM YY';
  start = new Date();
  end = new Date();
  dateRange = [this.start, this.end];
  startDate = '';
  endDate = '';
  private today = new Date();
  private year = this.today.getFullYear();
  completionCount = 0;
  by = 0;

  private defaultSteps: Step[] = [];
  ranges: { [key: string]: Date[] } = {};
  private langChangeSub!: Subscription;

  constructor(
    private dashboardService: DashboardService,
    private loading: LoadingService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private settingService: SettingService,
    private chargebeeService: ChargebeeService,
    private messageService: NzMessageService,
    private ph: PosthogService,
    private privilegeService: PrivilegeService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initializeTranslations();
    this.steps = [...this.defaultSteps];
    this.langChangeSub = this.translocoService.events$
      .pipe(filter((e: TranslocoEvents) => e.type === 'translationLoadSuccess'))
      .subscribe(() => {
        this.initializeTranslations();
        this.updateSteps();
      });
    this.findBrand().then(async (r: any) => {
      const user = await this.authService.getLoggedInUser();
      this.ph.identifyUser(user.email);
      const { payment } = r;
      const subList = await this.chargebeeService.getSubscriptionByCustomerId(
        payment.customerId
      );
      if (subList?.length > 0) {
        if (subList[0].status === 'cancelled') {
          this.determineRedirect(user, 'admin/payment/portal');
        }
      } else {
        this.determineRedirect(user, 'admin/payment/sub');
      }
    });
    this.start = new Date(moment().startOf('month').format());
    this.dateRange = [this.start, this.end];
    this.onDateChange(this.dateRange).then();
    this.fetchGetStartedMetrics().then();
  }

  async onDateChange(event: any): Promise<any> {
    event[0] = moment(event[0]).startOf('day').format();
    event[1] = moment(event[1]).endOf('day').format();
    this.startDate = moment(event[0]).format('DD MMM YYYY');
    this.endDate = moment(event[1]).format('DD MMM YYYY');
    await this.findItinerary(event);
    await this.findItineraryCount(event);
    await this.getItinerariesByUserIdCount(event);
  }

  async findItinerary(date: any): Promise<any> {
    this.loading.start();
    try {
      this.itineraries = await this.dashboardService.getItineraries({
        start: date[0],
        end: date[1],
      });
      this.loading.stop();
    } catch (e) {
      this.loading.stop();
    }
  }

  async findItineraryCount(date: any): Promise<any> {
    this.loading.start();
    try {
      this.itineraryCount = await this.dashboardService.getItinerariesCount({
        start: date[0],
        end: date[1],
      });
      this.loading.stop();
    } catch (e) {
      this.loading.stop();
    }
  }

  async getItinerariesByUserIdCount(date: any): Promise<any> {
    this.loading.start();
    try {
      this.salesByUsers =
        await this.dashboardService.getItinerariesByUserIdCount({
          start: date[0],
          end: date[1],
        });
      this.loading.stop();
    } catch (e) {
      this.loading.stop();
    }
  }

  itineraryDateCalculation(date: any): any {
    const today: any = new Date();
    const date2: any = new Date(date);
    const diffInMs = Math.abs(
      this.rearrangeDateFormat(date2) - this.rearrangeDateFormat(today)
    );
    const dayDiff = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const t = (key: string) => this.translocoService.translate(key);
    return dayDiff === 0
      ? t('dashboard.today')
      : dayDiff === 1
      ? t('dashboard.yesterday')
      : dayDiff + ' ' + t('dashboard.daysAgo');
  }

  rearrangeDateFormat(date: Date): any {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  onClickNavigateToPreview(inti: any): void {
    const theme = SETTINGS.THEME_TYPES_NAVI_B2C.find(
      theme => theme.value === inti.tour.themeType
    );
    const params = new URLSearchParams({ rretMgxg: 'ffliwqadrjt' });
    const baseUrl = this.getHostnameUrl();
    window.open(
      `${baseUrl}${theme?.url}/${inti._id}/false?${params.toString()}`,
      '_blank'
    );
  }

  getHostnameUrl(): string {
    return this.settingService.hostnameUrl || environment.previewLink;
  }

  async findBrand(): Promise<any> {
    try {
      const user = await this.authService.getLoggedInUser();
      return await this.settingService.findBrandPayment(user.userId);
    } catch (e: any) {}
  }

  determineRedirect(user: any, redirectTo: string): void {
    if (user.role.isSuperAdmin) {
      this.messageService.error(
        this.translocoService.translate('dashboard.messages.accountDisabled')
      );
      this.router.navigate([redirectTo]);
    } else {
      this.messageService.error(
        this.translocoService.translate('dashboard.messages.accountDisabledContact')
      );
      this.authService.logout();
    }
  }

  handleAction(actionKey: string) {
    switch (actionKey) {
      case 'resendEmail':
        this.resendEmail();
      break;

      case 'goToCompany':
        if (this.hasPermission('SETTING.COMPANY.INFORMATION.UPDATE')) {
          this.router.navigate(['/admin/settings'], {
            queryParams: { tab: 'branding', isTour: true },
          });
        } else {
          this.messageService.error(this.translocoService.translate('dashboard.messages.noPermissionView'));
        }
        break;

      case 'createItinerary':
        if (this.hasPermission('ITINERARY.ADD')) {
          this.router.navigate(['/admin/itineraries/add'], {
            queryParams: { tour: true }
          });
        } else {
          this.messageService.error(this.translocoService.translate('dashboard.messages.noPermissionCreate'));
        }
        break;
    }
  }

  async resendEmail() {
    try {
      this.loading.start();
      const user = await this.authService.getLoggedInUser();
      await this.userService.sendEmailVerification({ email: user.email });
      this.loading.stop();
      this.ngOnInit();
      this.messageService.success(this.translocoService.translate('dashboard.messages.verificationEmailSent'));
    } catch (error: any) {
      this.loading.stop();
      this.messageService.error(
        error.error?.message || this.translocoService.translate('dashboard.messages.failedToSendEmail')
      );
    }
  }

  async fetchGetStartedMetrics(): Promise<void> {
    try {
      this.getStartedMetrics = await this.dashboardService.getStartedMetrics();
      this.updateSteps();
    } catch (e) {
      console.error('Failed to fetch get started metrics', e);
    }
  }

  private updateSteps(): void {
    const isVerified = this.getStartedMetrics?.isVerified;
    const isAccountSetup = this.getStartedMetrics?.isAccountSetup;
    const isItineraryCreated = this.getStartedMetrics?.isItineraryCreated;
    this.completionCount = this.getStartedMetrics?.count || 0;
    this.by = this.getStartedMetrics?.by || 0;

    const t = (key: string) => this.translocoService.translate(key);
    this.steps = [
      this.defaultSteps[0],
      {
        ...this.defaultSteps[1],
        icon: isVerified ? 'check-circle' : 'mail',
        iconBg: isVerified ? 'bg-green-100' : 'bg-amber-100',
        iconColor: isVerified ? 'text-green-500' : 'text-amber-500',
        action: isVerified ? '' : t('dashboard.getStarted.resendEmail'),
      },
      {
        ...this.defaultSteps[2],
        icon: isAccountSetup ? 'check-circle' : 'idcard',
        iconBg: isAccountSetup ? 'bg-green-100' : 'bg-blue-100',
        iconColor: isAccountSetup ? 'text-green-500' : 'text-blue-500',
        action: isAccountSetup ? '' : t('dashboard.getStarted.addCompanyInfo'),
      },
      {
        ...this.defaultSteps[3],
        icon: isItineraryCreated ? 'check-circle' : 'file-text',
        iconBg: isItineraryCreated ? 'bg-green-100' : 'bg-emerald-100',
        iconColor: isItineraryCreated ? 'text-green-500' : 'text-emerald-500',
        action: isItineraryCreated ? '' : t('dashboard.getStarted.createItineraryBtn'),
      },
    ];
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  protected readonly SETTINGS = SETTINGS;

  closeBanner(): void {
    if (this.getStartedMetrics) {
      this.getStartedMetrics.isShowBanner = false;
    }
  }

  hasPermission(permission: string): boolean {
    return this.privilegeService.hasPrivilege(permission);
  }

  private initializeTranslations(): void {
    const t = (key: string) => this.translocoService.translate(key);

    this.defaultSteps = [
      {
        icon: 'rocket',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-500',
        title: t('dashboard.getStarted.title'),
        description: t('dashboard.getStarted.description'),
        action: '',
        actionKey: '',
      },
      {
        icon: 'mail',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
        title: t('dashboard.getStarted.verifyEmail'),
        description: t('dashboard.getStarted.verifyEmailDesc'),
        action: t('dashboard.getStarted.resendEmail'),
        actionKey: 'resendEmail',
      },
      {
        icon: 'idcard',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-500',
        title: t('dashboard.getStarted.setupCompanyInfo'),
        description: t('dashboard.getStarted.setupCompanyInfoDesc'),
        action: t('dashboard.getStarted.addCompanyInfo'),
        actionKey: 'goToCompany',
      },
      {
        icon: 'file-text',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-500',
        title: t('dashboard.getStarted.createItinerary'),
        description: t('dashboard.getStarted.createItineraryDesc'),
        action: t('dashboard.getStarted.createItineraryBtn'),
        actionKey: 'createItinerary',
      },
    ];

    this.ranges = {
      [t('dashboard.dateRanges.today')]: [this.today, this.today],
      [t('dashboard.dateRanges.thisWeek')]: [
        startOfWeek(this.today, { weekStartsOn: 1 }),
        endOfWeek(this.today, { weekStartsOn: 1 }),
      ],
      [t('dashboard.dateRanges.thisMonth')]: [startOfMonth(this.today), endOfMonth(this.today)],
      [t('dashboard.dateRanges.thisQuarter')]: [startOfQuarter(this.today), endOfQuarter(this.today)],
      [t('dashboard.dateRanges.thisYear')]: [startOfYear(this.today), endOfYear(this.today)],
      [t('dashboard.dateRanges.q1')]: [new Date(this.year, 0, 1), new Date(this.year, 2, 31)],
      [t('dashboard.dateRanges.q2')]: [new Date(this.year, 3, 1), new Date(this.year, 5, 30)],
      [t('dashboard.dateRanges.q3')]: [new Date(this.year, 6, 1), new Date(this.year, 8, 30)],
      [t('dashboard.dateRanges.q4')]: [new Date(this.year, 9, 1), new Date(this.year, 11, 31)],
    };
  }
}
